import React, { useState, useEffect } from 'react';

// AI 거래 설정 모달 컴포넌트
const AITradeConfigModal = ({ config, onClose, onSave, authenticatedFetch, user, onOpen }) => {
  const isAdmin = user?.email === 'begite@naver.com';
  const hasEnvKey = !!config?.using_env_key;
  const hasStoredKey = !!config?.google_api_key;
  const [usageStats, setUsageStats] = useState(null);
  const [loadingUsageStats, setLoadingUsageStats] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [loadingConnectionStatus, setLoadingConnectionStatus] = useState(false);
  const [showUsageStatsBox, setShowUsageStatsBox] = useState(false); // 사용량 통계 박스: 기본 감추기, 클릭 시 보이기
  const [showAutoTradeSettings, setShowAutoTradeSettings] = useState(false); // 자동 거래 실행 설정: 기본 감추기, 클릭 시 보이기
  const [showBalanceLimits, setShowBalanceLimits] = useState(false); // 잔고 및 포트폴리오 제한 설정: 기본 감추기, 클릭 시 보이기
  
  // 사용량 통계 조회
  const fetchUsageStats = async () => {
    try {
      setLoadingUsageStats(true);
      const response = await authenticatedFetch('/api/ai-trade/usage-stats?days=30', {
        timeout: 120000 // 120초 - AI 관련
      });
      if (response.ok) {
        const data = await response.json();
        setUsageStats(data);
      }
    } catch (error) {
      console.error('사용량 통계 조회 실패:', error);
    } finally {
      setLoadingUsageStats(false);
    }
  };
  
  // AI 연결 상태 확인
  const fetchConnectionStatus = async () => {
    try {
      setLoadingConnectionStatus(true);
      // 타임아웃을 20초로 증가 (병렬 처리로 최대 8초 + 여유 시간)
      const response = await authenticatedFetch('/api/ai-trade/connection-status', {
        timeout: 20000
      });
      if (response.ok) {
        const data = await response.json();
        setConnectionStatus(data);
      } else {
        // HTTP 에러 응답 처리
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${response.status}`);
      }
    } catch (error) {
      console.error('연결 상태 확인 실패:', error);
      setConnectionStatus({
        google_ai: {
          connected: false,
          message: `❌ 연결 상태 확인 실패: ${error.message || '알 수 없는 오류'}`,
          error: error.message || 'Unknown error'
        },
        local_ai: {
          connected: false,
          message: `❌ 연결 상태 확인 실패: ${error.message || '알 수 없는 오류'}`,
          error: error.message || 'Unknown error'
        },
        timestamp: new Date().toISOString()
      });
    } finally {
      setLoadingConnectionStatus(false);
    }
  };
  
  // 모달이 열릴 때 설정 조회, 사용량 통계 조회 및 연결 상태 확인
  useEffect(() => {
    if (onOpen) {
      onOpen();
    }
    fetchUsageStats();
    fetchConnectionStatus();
  }, []); // 컴포넌트 마운트 시 한 번만 실행
  
  // config가 변경되면 formData 업데이트
  useEffect(() => {
    if (config) {
      setFormData(prev => ({
        ...prev,
        is_active: config.is_active || false,
        investment_strategy: config.investment_strategy || 'custom',
        analysis_period_days: config.analysis_config?.analysis_period_days || 365,
        analysis_depth: config.analysis_config?.analysis_depth || 'HIGH',
        include_web_search: config.analysis_config?.include_web_search !== false,
        include_company_data: config.analysis_config?.include_company_data !== false,
        update_frequency_weeks: config.analysis_config?.update_frequency_weeks || 1,
        // 자동 거래 설정 (백엔드에서 평탄화되어 반환되므로 config.auto_buy로 접근)
        auto_buy_enabled: config.auto_buy?.enabled ?? config.metadata?.auto_buy?.enabled ?? false,
        auto_buy_confidence_threshold: config.auto_buy?.confidence_threshold ?? config.metadata?.auto_buy?.confidence_threshold ?? 70,
        auto_buy_quantity_percent: config.auto_buy?.quantity_percent ?? config.metadata?.auto_buy?.quantity_percent ?? 10,
        auto_buy_max_daily_purchases: config.auto_buy?.max_daily_purchases ?? config.metadata?.auto_buy?.max_daily_purchases ?? 5,
        auto_sell_enabled: config.auto_sell?.enabled ?? config.metadata?.auto_sell?.enabled ?? false,
        auto_sell_confidence_threshold: config.auto_sell?.confidence_threshold ?? config.metadata?.auto_sell?.confidence_threshold ?? 70,
        auto_sell_quantity_percent: config.auto_sell?.quantity_percent ?? config.metadata?.auto_sell?.quantity_percent ?? 50,
        // 손실 트리거 설정
        loss_trigger_enabled: config.auto_sell?.loss_trigger?.enabled ?? false,
        loss_trigger_percent_threshold: config.auto_sell?.loss_trigger?.percent_threshold ?? 3,
        loss_trigger_amount_threshold: config.auto_sell?.loss_trigger?.amount_threshold ?? 0,
        loss_trigger_sell_percentage: config.auto_sell?.loss_trigger?.sell_percentage ?? 50,
        loss_trigger_order_method: config.auto_sell?.loss_trigger?.order_method ?? 'market',
        loss_trigger_min_holding_days: config.auto_sell?.loss_trigger?.min_holding_days ?? 0,
        loss_trigger_action: config.auto_sell?.loss_trigger?.action ?? 'force',
        loss_trigger_check_interval_minutes: config.auto_sell?.loss_trigger?.check_interval_minutes ?? 15,
        // 즉시 실행 설정
        execute_immediately: config.execute_immediately ?? config.metadata?.execute_immediately ?? false,
        schedule_mode: config.execution_schedule?.mode || 'delay',
        schedule_delay_minutes: config.execution_schedule?.delay_minutes ?? 5,
        schedule_window_start: config.execution_schedule?.window_start || '09:05',
        schedule_window_end: config.execution_schedule?.window_end || '15:20',
        schedule_weekdays: Array.isArray(config.execution_schedule?.weekdays) ? config.execution_schedule.weekdays : [1, 2, 3, 4, 5],
        schedule_market_hours: config.execution_schedule?.enforce_market_hours ?? true,
        // 잔고 제한 설정
        max_balance_usage_percent: config.auto_buy?.balance_limits?.max_balance_usage_percent ?? config.metadata?.auto_buy?.balance_limits?.max_balance_usage_percent ?? 100,
        reserved_balance: config.auto_buy?.balance_limits?.reserved_balance ?? config.metadata?.auto_buy?.balance_limits?.reserved_balance ?? 0,
        designated_cash_balance: config.auto_buy?.balance_limits?.designated_cash_balance ?? config.metadata?.auto_buy?.balance_limits?.designated_cash_balance ?? 0,
        max_holding_percent: config.auto_buy?.max_holding_percent ?? config.metadata?.auto_buy?.max_holding_percent ?? 20,
        max_holdings_count: config.auto_buy?.max_holdings_count ?? config.metadata?.auto_buy?.max_holdings_count ?? 20,
        min_investment_amount: config.auto_buy?.min_investment_amount ?? config.metadata?.auto_buy?.min_investment_amount ?? 100000,
        // 가격 변동률 설정
        max_price_change_percent: config.auto_buy?.max_price_change_percent ?? config.metadata?.auto_buy?.max_price_change_percent ?? 10,
        adjust_price_on_volatility: config.auto_buy?.adjust_price_on_volatility ?? config.metadata?.auto_buy?.adjust_price_on_volatility ?? true,
        // 사용자 프로필 정보
        profile_visible: config.user_profile?.profile_visible !== false,
        risk_tolerance: config.user_profile?.risk_tolerance || '',
        monthly_income: config.user_profile?.financial_status?.monthly_income || 0,
        investable_assets: config.user_profile?.financial_status?.investable_assets || 0,
        investment_experience_years: config.user_profile?.financial_status?.investment_experience_years || 0,
        primary_goal: config.user_profile?.investment_goals?.primary_goal || '',
        time_horizon: config.user_profile?.investment_goals?.time_horizon || '',
        target_return_percent: config.user_profile?.investment_goals?.target_return_percent || 0,
        profit_target_sell_mode: config.user_profile?.investment_goals?.profit_target_sell_mode || 'immediate',
        profit_target_downturn_lookback_days: config.user_profile?.investment_goals?.profit_target_downturn_lookback_days ?? 5,
        profit_target_downturn_threshold_percent: config.user_profile?.investment_goals?.profit_target_downturn_threshold_percent ?? 2,
        preferred_sectors: config.user_profile?.preferences?.preferred_sectors || [],
        excluded_sectors: config.user_profile?.preferences?.excluded_sectors || [],
        preferred_market_cap: config.user_profile?.preferences?.preferred_market_cap || '',
        max_position_size_percent: config.user_profile?.preferences?.max_position_size_percent || 20
      }));
      
      // showUserProfile 상태 업데이트
      setShowUserProfile(config.user_profile?.profile_visible !== false);
      
      // 선호/제외 업종 입력 필드 초기화
      setPreferredSectorsInput((config.user_profile?.preferences?.preferred_sectors || []).join(', '));
      setExcludedSectorsInput((config.user_profile?.preferences?.excluded_sectors || []).join(', '));
      
      console.log('✅ [AI 설정 모달] 사용자 투자 프로필 로드 완료:', {
        profile_visible: config.user_profile?.profile_visible !== false,
        risk_tolerance: config.user_profile?.risk_tolerance || '(미설정)',
        monthly_income: config.user_profile?.financial_status?.monthly_income || 0,
        investable_assets: config.user_profile?.financial_status?.investable_assets || 0,
        preferred_sectors_count: (config.user_profile?.preferences?.preferred_sectors || []).length,
        excluded_sectors_count: (config.user_profile?.preferences?.excluded_sectors || []).length
      });
    }
  }, [config]);
  
  const [formData, setFormData] = useState({
    // 관리자이고 .env 키가 있으면 입력란을 비워둠 (백엔드에서 .env 키 자동 사용)
    google_api_key: (isAdmin && hasEnvKey) ? '' : (config?.google_api_key?.replace('***', '') || ''),
    is_active: config?.is_active || false,
    investment_strategy: config?.investment_strategy || 'custom',
    analysis_period_days: config?.analysis_config?.analysis_period_days || 365,
    analysis_depth: config?.analysis_config?.analysis_depth || 'HIGH',
    include_web_search: config?.analysis_config?.include_web_search !== false,
    include_company_data: config?.analysis_config?.include_company_data !== false,
    update_frequency_weeks: config?.analysis_config?.update_frequency_weeks || 1,
    // 자동 거래 설정 (백엔드에서 평탄화되어 반환되므로 config.auto_buy로 접근)
    auto_buy_enabled: config?.auto_buy?.enabled ?? config?.metadata?.auto_buy?.enabled ?? false,
    auto_buy_confidence_threshold: config?.auto_buy?.confidence_threshold ?? config?.metadata?.auto_buy?.confidence_threshold ?? 70,
    auto_buy_quantity_percent: config?.auto_buy?.quantity_percent ?? config?.metadata?.auto_buy?.quantity_percent ?? 10,
    auto_buy_max_daily_purchases: config?.auto_buy?.max_daily_purchases ?? config?.metadata?.auto_buy?.max_daily_purchases ?? 5,
    auto_sell_enabled: config?.auto_sell?.enabled ?? config?.metadata?.auto_sell?.enabled ?? false,
    auto_sell_confidence_threshold: config?.auto_sell?.confidence_threshold ?? config?.metadata?.auto_sell?.confidence_threshold ?? 70,
    auto_sell_quantity_percent: config?.auto_sell?.quantity_percent ?? config?.metadata?.auto_sell?.quantity_percent ?? 50,
    // 손실 트리거 설정
    loss_trigger_enabled: config?.auto_sell?.loss_trigger?.enabled ?? false,
    loss_trigger_percent_threshold: config?.auto_sell?.loss_trigger?.percent_threshold ?? 3,
    loss_trigger_amount_threshold: config?.auto_sell?.loss_trigger?.amount_threshold ?? 0,
    loss_trigger_sell_percentage: config?.auto_sell?.loss_trigger?.sell_percentage ?? 50,
    loss_trigger_order_method: config?.auto_sell?.loss_trigger?.order_method ?? 'market',
    loss_trigger_min_holding_days: config?.auto_sell?.loss_trigger?.min_holding_days ?? 0,
    loss_trigger_action: config?.auto_sell?.loss_trigger?.action ?? 'force',
    loss_trigger_check_interval_minutes: config?.auto_sell?.loss_trigger?.check_interval_minutes ?? 15,
    // 즉시 실행 설정
    execute_immediately: config?.execute_immediately ?? config?.metadata?.execute_immediately ?? false,
    schedule_mode: config?.execution_schedule?.mode || 'delay',
    schedule_delay_minutes: config?.execution_schedule?.delay_minutes ?? 5,
    schedule_window_start: config?.execution_schedule?.window_start || '09:05',
    schedule_window_end: config?.execution_schedule?.window_end || '15:20',
    schedule_weekdays: Array.isArray(config?.execution_schedule?.weekdays) ? config.execution_schedule.weekdays : [1, 2, 3, 4, 5],
    schedule_market_hours: config?.execution_schedule?.enforce_market_hours ?? true,
    // 잔고 제한 설정
    max_balance_usage_percent: config?.auto_buy?.balance_limits?.max_balance_usage_percent ?? config?.metadata?.auto_buy?.balance_limits?.max_balance_usage_percent ?? 100,
    reserved_balance: config?.auto_buy?.balance_limits?.reserved_balance ?? config?.metadata?.auto_buy?.balance_limits?.reserved_balance ?? 0,
    designated_cash_balance: config?.auto_buy?.balance_limits?.designated_cash_balance ?? config?.metadata?.auto_buy?.balance_limits?.designated_cash_balance ?? 0,
        max_holding_percent: config?.auto_buy?.max_holding_percent ?? config?.metadata?.auto_buy?.max_holding_percent ?? 20,
        max_holdings_count: config?.auto_buy?.max_holdings_count ?? config?.metadata?.auto_buy?.max_holdings_count ?? 20,
        min_investment_amount: config?.auto_buy?.min_investment_amount ?? config?.metadata?.auto_buy?.min_investment_amount ?? 100000,
        // 가격 변동률 설정
        max_price_change_percent: config?.auto_buy?.max_price_change_percent ?? config?.metadata?.auto_buy?.max_price_change_percent ?? 10,
    adjust_price_on_volatility: config?.auto_buy?.adjust_price_on_volatility ?? config?.metadata?.auto_buy?.adjust_price_on_volatility ?? true,
    // 사용자 프로필 정보
    profile_visible: config?.user_profile?.profile_visible !== false,
    risk_tolerance: config?.user_profile?.risk_tolerance || '',
    monthly_income: config?.user_profile?.financial_status?.monthly_income || 0,
    investable_assets: config?.user_profile?.financial_status?.investable_assets || 0,
    investment_experience_years: config?.user_profile?.financial_status?.investment_experience_years || 0,
    primary_goal: config?.user_profile?.investment_goals?.primary_goal || '',
    time_horizon: config?.user_profile?.investment_goals?.time_horizon || '',
    target_return_percent: config?.user_profile?.investment_goals?.target_return_percent || 0,
    profit_target_sell_mode: config?.user_profile?.investment_goals?.profit_target_sell_mode || 'immediate',
    profit_target_downturn_lookback_days: config?.user_profile?.investment_goals?.profit_target_downturn_lookback_days ?? 5,
    profit_target_downturn_threshold_percent: config?.user_profile?.investment_goals?.profit_target_downturn_threshold_percent ?? 2,
    preferred_sectors: config?.user_profile?.preferences?.preferred_sectors || [],
    excluded_sectors: config?.user_profile?.preferences?.excluded_sectors || [],
    preferred_market_cap: config?.user_profile?.preferences?.preferred_market_cap || '',
    max_position_size_percent: config?.user_profile?.preferences?.max_position_size_percent || 20
  });
  const [showUserProfile, setShowUserProfile] = useState(formData.profile_visible);
  const [loading, setLoading] = useState(false);
  const [showApiKey, setShowApiKey] = useState(false);
  const [showStopConfirm, setShowStopConfirm] = useState(false);
  const [aiTrades, setAiTrades] = useState([]);
  const [tradeStats, setTradeStats] = useState(null);
  const [loadingTrades, setLoadingTrades] = useState(false);
  const [activeAnalysisTab, setActiveAnalysisTab] = useState('portfolio'); // portfolio, recommended
  const [portfolioAnalyses, setPortfolioAnalyses] = useState([]);
  const [recommendedAnalyses, setRecommendedAnalyses] = useState([]);
  const [loadingAnalyses, setLoadingAnalyses] = useState(false);
  const [selectedAnalysis, setSelectedAnalysis] = useState(null);
  const [showAnalysisDetail, setShowAnalysisDetail] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  // 선호사항 입력을 위한 별도 상태 (입력 중에는 문자열로 유지)
  const [preferredSectorsInput, setPreferredSectorsInput] = useState('');
  const [excludedSectorsInput, setExcludedSectorsInput] = useState('');

  // AI 거래 내역 조회
  const fetchAiTrades = async () => {
    setLoadingTrades(true);
    try {
      const response = await authenticatedFetch('/api/ai-trade/trades?limit=100');
      if (response.ok) {
        const data = await response.json();
        setAiTrades(data.trades || []);
        setTradeStats(data.stats || null);
      }
    } catch (error) {
      console.error('AI 거래 내역 조회 실패:', error);
    } finally {
      setLoadingTrades(false);
    }
  };

  // 포트폴리오 분석 결과 조회
  const fetchPortfolioAnalyses = async () => {
    setLoadingAnalyses(true);
    try {
      const response = await authenticatedFetch('/api/ai-trade/analyses?status=보유중&limit=50', {
        timeout: 120000 // 120초 - 분석 리스트 조회
      });
      if (response.ok) {
        const data = await response.json();
        setPortfolioAnalyses(data.analyses || []);
        // 자동거래 설정 정보 저장 (표시용)
        if (data.config) {
          console.log('📋 자동거래 설정:', data.config);
        }
      }
    } catch (error) {
      console.error('포트폴리오 분석 조회 실패:', error);
      if (error.name === 'TimeoutError' || error.status === 504) {
        console.warn('⚠️ 포트폴리오 분석 조회 타임아웃 - 잠시 후 다시 시도해주세요.');
      }
    } finally {
      setLoadingAnalyses(false);
    }
  };

  // AI 추천 종목 분석 결과 조회
  const fetchRecommendedAnalyses = async () => {
    setLoadingAnalyses(true);
    try {
      const response = await authenticatedFetch('/api/ai-trade/analyses?status=구매예정&limit=50', {
        timeout: 120000 // 120초 - 분석 리스트 조회
      });
      if (response.ok) {
        const data = await response.json();
        setRecommendedAnalyses(data.analyses || []);
        // 자동거래 설정 정보 저장 (표시용)
        if (data.config) {
          console.log('📋 자동거래 설정:', data.config);
        }
      }
    } catch (error) {
      console.error('AI 추천 종목 분석 조회 실패:', error);
    } finally {
      setLoadingAnalyses(false);
    }
  };

  // AI 거래 계획 수정
  const updateTradePlan = async (analysisId, tradeConfig) => {
    try {
      const response = await authenticatedFetch(`/api/ai-trade/analyses/${analysisId}/plan`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ trade_config: tradeConfig })
      });

      if (response.ok) {
        const data = await response.json();
        alert('AI 거래 계획이 수정되었습니다.');
        // 분석 목록 새로고침
        if (activeAnalysisTab === 'portfolio') {
          fetchPortfolioAnalyses();
        } else {
          fetchRecommendedAnalyses();
        }
        setEditingPlan(null);
        return data;
      } else {
        const error = await response.json();
        alert(error.error || '계획 수정에 실패했습니다.');
        throw new Error(error.error);
      }
    } catch (error) {
      console.error('AI 거래 계획 수정 실패:', error);
      throw error;
    }
  };

  // 탭 변경 시 분석 결과 조회
  useEffect(() => {
    if (activeAnalysisTab === 'portfolio') {
      fetchPortfolioAnalyses();
    } else if (activeAnalysisTab === 'recommended') {
      fetchRecommendedAnalyses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeAnalysisTab]);

  // 중단 확인 모달 열기
  const handleStopClick = async () => {
    if (!formData.is_active) {
      alert('AI 자동거래가 이미 비활성화되어 있습니다.');
      return;
    }
    await fetchAiTrades();
    setShowStopConfirm(true);
  };

  // AI 자동거래 중단 처리
  const handleStopConfirm = async () => {
    setLoading(true);
    try {
      // preferred_sectors와 excluded_sectors를 배열로 변환
      const preferredSectorsArray = preferredSectorsInput
        ? preferredSectorsInput.split(',').map(s => s.trim()).filter(s => s.length > 0)
        : [];
      const excludedSectorsArray = excludedSectorsInput
        ? excludedSectorsInput.split(',').map(s => s.trim()).filter(s => s.length > 0)
        : [];

      const response = await authenticatedFetch('/api/ai-trade/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        timeout: 120000, // 120초 - AI 설정 저장
        body: JSON.stringify({
          google_api_key: formData.google_api_key,
          is_active: false,
          execution_schedule: {
            mode: formData.schedule_mode,
            delay_minutes: formData.schedule_delay_minutes,
            window_start: formData.schedule_window_start,
            window_end: formData.schedule_window_end,
            weekdays: formData.schedule_weekdays,
            enforce_market_hours: formData.schedule_market_hours,
            timezone: 'Asia/Seoul'
          },
          analysis_config: {
            analysis_period_days: formData.analysis_period_days,
            analysis_depth: formData.analysis_depth,
            include_web_search: formData.include_web_search,
            include_company_data: formData.include_company_data,
            update_frequency_weeks: formData.update_frequency_weeks
          },
          user_profile: {
            profile_visible: showUserProfile,
            risk_tolerance: formData.risk_tolerance || '',
            financial_status: {
              monthly_income: formData.monthly_income || 0,
              investable_assets: formData.investable_assets || 0,
              investment_experience_years: formData.investment_experience_years || 0
            },
            investment_goals: {
              primary_goal: formData.primary_goal || '',
              time_horizon: formData.time_horizon || '',
              target_return_percent: formData.target_return_percent || 0,
              profit_target_sell_mode: formData.profit_target_sell_mode || 'immediate',
              profit_target_downturn_lookback_days: formData.profit_target_downturn_lookback_days ?? 5,
              profit_target_downturn_threshold_percent: formData.profit_target_downturn_threshold_percent ?? 2
            },
            preferences: {
              preferred_sectors: preferredSectorsArray,
              excluded_sectors: excludedSectorsArray,
              preferred_market_cap: formData.preferred_market_cap || '',
              max_position_size_percent: formData.max_position_size_percent || 20
            }
          },
          metadata: {
            auto_buy: {
              enabled: formData.auto_buy_enabled,
              confidence_threshold: formData.auto_buy_confidence_threshold,
              quantity_percent: formData.auto_buy_quantity_percent,
              balance_limits: {
                max_balance_usage_percent: formData.max_balance_usage_percent,
                reserved_balance: formData.reserved_balance,
                designated_cash_balance: formData.designated_cash_balance
              },
              max_holding_percent: formData.max_holding_percent,
              max_holdings_count: formData.max_holdings_count,
              min_investment_amount: formData.min_investment_amount ?? 100000,
              max_price_change_percent: formData.max_price_change_percent,
              adjust_price_on_volatility: formData.adjust_price_on_volatility
            },
            auto_sell: {
              enabled: formData.auto_sell_enabled,
              confidence_threshold: formData.auto_sell_confidence_threshold,
              quantity_percent: formData.auto_sell_quantity_percent
            },
            execute_immediately: formData.execute_immediately
          }
        })
      });

      if (response.ok) {
        // const data = await response.json(); // 미사용 변수 제거
        setFormData({ ...formData, is_active: false });
        setShowStopConfirm(false);
        alert('AI 자동거래가 중단되었습니다.');
        onSave();
      } else {
        const error = await response.json();
        alert(error.error || '중단 처리에 실패했습니다.');
      }
    } catch (error) {
      console.error('AI 거래 중단 실패:', error);
      alert('중단 처리 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 키 검증 로직 단순화: is_active가 true일 때만 검증
    if (formData.is_active && !formData.google_api_key) {
      // 관리자인 경우 .env 키가 있으면 자동으로 사용됨을 안내
      if ((isAdmin && hasEnvKey) || hasStoredKey) {
        // .env 키가 있으면 자동으로 사용되므로 경고 없이 저장 가능
      } else {
        // 일반 사용자이거나 관리자지만 .env 키가 없으면 경고
        alert('AI 자동거래를 활성화하려면 Google AI API 키가 필요합니다.');
        return;
      }
    }

    setLoading(true);
    try {
      // 관리자인 경우 키가 비어있으면 undefined로 전송 (백엔드에서 .env 키 자동 적용)
      // 일반 사용자는 빈 문자열이면 빈 문자열 전송
      const apiKeyToSend = (isAdmin && hasEnvKey && !formData.google_api_key) 
        ? undefined 
        : (formData.google_api_key || '');
      
      // preferred_sectors와 excluded_sectors를 배열로 변환
      const preferredSectorsArray = preferredSectorsInput
        ? preferredSectorsInput.split(',').map(s => s.trim()).filter(s => s.length > 0)
        : [];
      const excludedSectorsArray = excludedSectorsInput
        ? excludedSectorsInput.split(',').map(s => s.trim()).filter(s => s.length > 0)
        : [];

      const requestBody = {
        investment_strategy: formData.investment_strategy,
        is_active: formData.is_active,
        analysis_config: {
          analysis_period_days: formData.analysis_period_days,
          analysis_depth: formData.analysis_depth,
          include_web_search: formData.include_web_search,
          include_company_data: formData.include_company_data,
          update_frequency_weeks: formData.update_frequency_weeks
        },
        user_profile: {
          profile_visible: showUserProfile,
          risk_tolerance: formData.risk_tolerance || '',
          financial_status: {
            monthly_income: formData.monthly_income || 0,
            investable_assets: formData.investable_assets || 0,
            investment_experience_years: formData.investment_experience_years || 0
          },
          investment_goals: {
            primary_goal: formData.primary_goal || '',
            time_horizon: formData.time_horizon || '',
            target_return_percent: formData.target_return_percent || 0,
            profit_target_sell_mode: formData.profit_target_sell_mode || 'immediate',
            profit_target_downturn_lookback_days: formData.profit_target_downturn_lookback_days ?? 5,
            profit_target_downturn_threshold_percent: formData.profit_target_downturn_threshold_percent ?? 2
          },
          preferences: {
            preferred_sectors: preferredSectorsArray,
            excluded_sectors: excludedSectorsArray,
            preferred_market_cap: formData.preferred_market_cap || '',
            max_position_size_percent: formData.max_position_size_percent || 20
          }
        },
        auto_buy: {
          enabled: formData.auto_buy_enabled,
          confidence_threshold: formData.auto_buy_confidence_threshold,
          quantity_percent: formData.auto_buy_quantity_percent,
          max_daily_purchases: formData.auto_buy_max_daily_purchases || 5,
          max_holding_percent: formData.max_holding_percent || 20,
          max_holdings_count: formData.max_holdings_count || 20,
          min_investment_amount: formData.min_investment_amount ?? 100000,
          max_price_change_percent: formData.max_price_change_percent ?? 10,
          adjust_price_on_volatility: formData.adjust_price_on_volatility ?? true,
          balance_limits: {
            max_balance_usage_percent: formData.max_balance_usage_percent || 80,
            reserved_balance: formData.reserved_balance || 0,
            designated_cash_balance: formData.designated_cash_balance || 0
          }
        },
        auto_sell: {
          enabled: formData.auto_sell_enabled,
          confidence_threshold: formData.auto_sell_confidence_threshold,
          quantity_percent: formData.auto_sell_quantity_percent,
          max_price_change_percent: formData.auto_sell_max_price_change_percent ?? formData.max_price_change_percent ?? 10,
          adjust_price_on_volatility: formData.auto_sell_adjust_price_on_volatility ?? formData.adjust_price_on_volatility ?? true,
          loss_trigger: {
            enabled: formData.loss_trigger_enabled,
            percent_threshold: formData.loss_trigger_percent_threshold,
            amount_threshold: formData.loss_trigger_amount_threshold,
            sell_percentage: formData.loss_trigger_sell_percentage,
            order_method: formData.loss_trigger_order_method,
            min_holding_days: formData.loss_trigger_min_holding_days,
            action: formData.loss_trigger_action,
            check_interval_minutes: formData.loss_trigger_check_interval_minutes
          }
        },
        execute_immediately: formData.execute_immediately,
        execution_schedule: {
          mode: formData.schedule_mode,
          delay_minutes: formData.schedule_delay_minutes,
          window_start: formData.schedule_window_start,
          window_end: formData.schedule_window_end,
          weekdays: formData.schedule_weekdays,
          enforce_market_hours: formData.schedule_market_hours,
          timezone: 'Asia/Seoul'
        }
      };
      
      console.log('📤 [AI 설정 모달] 저장 요청:', {
        has_user_profile: !!requestBody.user_profile,
        profile_visible: requestBody.user_profile.profile_visible,
        risk_tolerance: requestBody.user_profile.risk_tolerance,
        preferred_sectors_count: requestBody.user_profile.preferences.preferred_sectors.length,
        excluded_sectors_count: requestBody.user_profile.preferences.excluded_sectors.length,
        auto_buy_max_price_change_percent: requestBody.auto_buy?.max_price_change_percent,
        auto_buy_adjust_price_on_volatility: requestBody.auto_buy?.adjust_price_on_volatility,
        auto_sell_max_price_change_percent: requestBody.auto_sell?.max_price_change_percent,
        auto_sell_adjust_price_on_volatility: requestBody.auto_sell?.adjust_price_on_volatility
      });
      
      // API 키는 undefined가 아닐 때만 포함 (undefined면 백엔드에서 기존 키 유지 또는 .env 키 적용)
      if (apiKeyToSend !== undefined) {
        requestBody.google_api_key = apiKeyToSend;
      }
      
      const response = await authenticatedFetch('/api/ai-trade/config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        timeout: 120000, // 120초 - AI 설정 저장
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        alert(data.message || 'AI 거래 설정이 저장되었습니다.');
        onSave();
      } else {
        const error = await response.json();
        alert(error.error || '설정 저장에 실패했습니다.');
      }
    } catch (error) {
      console.error('AI 거래 설정 저장 실패:', error);
      alert('설정 저장 중 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      if (onClose) onClose();
    }
  };

  return (
    <div 
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 10000,
        paddingTop: '80px', // 헤더 높이만큼 상단 여백 추가
        boxSizing: 'border-box'
      }}
      onClick={handleOverlayClick}
    >
      <div 
        style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '8px',
          maxWidth: '700px',
          width: '90%',
          maxHeight: 'calc(100vh - 100px)', // 헤더 높이 고려하여 조정
          overflow: 'auto',
          position: 'relative',
          marginTop: 'auto',
          marginBottom: 'auto'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>🤖 AI 자동거래 설정</h2>
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (onClose) onClose();
            }}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              color: '#999',
              cursor: 'pointer',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: '1'
            }}
            aria-label="닫기"
          >
            ×
          </button>
        </div>
        
        {/* API 사용량 통계 및 연결 상태 - 상단에 표시 */}
        <div style={{ 
          marginBottom: '30px', 
          padding: '20px', 
          backgroundColor: '#f8f9fa', 
          borderRadius: '8px',
          border: '1px solid #dee2e6'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showUsageStatsBox ? '15px' : '0' }}>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>📊 API 사용량 통계 (최근 30일)</h3>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                type="button"
                onClick={() => setShowUsageStatsBox(!showUsageStatsBox)}
                style={{
                  padding: '5px 10px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '12px'
                }}
                title={showUsageStatsBox ? '박스 숨기기' : '박스 보이기'}
              >
                {showUsageStatsBox ? '👁️ 감추기' : '👁️ 보이기'}
              </button>
              <button
                type="button"
                onClick={fetchConnectionStatus}
                disabled={loadingConnectionStatus}
                style={{
                  padding: '5px 10px',
                  backgroundColor: loadingConnectionStatus ? '#ccc' : '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loadingConnectionStatus ? 'not-allowed' : 'pointer',
                  fontSize: '12px'
                }}
                title="AI 연결 상태 확인"
              >
                {loadingConnectionStatus ? '확인 중...' : '🔌 연결 확인'}
              </button>
              <button
                type="button"
                onClick={fetchUsageStats}
                disabled={loadingUsageStats}
                style={{
                  padding: '5px 10px',
                  backgroundColor: loadingUsageStats ? '#ccc' : '#667eea',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: loadingUsageStats ? 'not-allowed' : 'pointer',
                  fontSize: '12px'
                }}
              >
                {loadingUsageStats ? '로딩 중...' : '🔄 새로고침'}
              </button>
            </div>
          </div>
          
          {showUsageStatsBox && (
            <>
          
          {/* 연결 상태 표시 */}
          {connectionStatus && (
            <div style={{ 
              marginBottom: '20px', 
              padding: '12px', 
              backgroundColor: 'white', 
              borderRadius: '6px',
              border: '1px solid #e0e0e0'
            }}>
              <h4 style={{ margin: '0 0 10px 0', fontSize: '14px', fontWeight: 'bold' }}>🔌 AI 연결 상태</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '13px' }}>
                <div style={{ 
                  padding: '8px', 
                  backgroundColor: connectionStatus.google_ai?.connected ? '#d4edda' : '#f8d7da',
                  borderRadius: '4px',
                  border: `1px solid ${connectionStatus.google_ai?.connected ? '#c3e6cb' : '#f5c6cb'}`
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    {connectionStatus.google_ai?.connected ? '✅' : '❌'} Google AI
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {connectionStatus.google_ai?.message || '상태 확인 중...'}
                    {connectionStatus.google_ai?.model && (
                      <div style={{ marginTop: '4px', fontSize: '11px', color: '#999' }}>
                        모델: {connectionStatus.google_ai.model}
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ 
                  padding: '8px', 
                  backgroundColor: connectionStatus.local_ai?.connected ? '#d4edda' : '#f8d7da',
                  borderRadius: '4px',
                  border: `1px solid ${connectionStatus.local_ai?.connected ? '#c3e6cb' : '#f5c6cb'}`
                }}>
                  <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                    {connectionStatus.local_ai?.connected ? '✅' : '❌'} Local AI
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {connectionStatus.local_ai?.message || '상태 확인 중...'}
                    {connectionStatus.local_ai?.model && (
                      <div style={{ marginTop: '4px', fontSize: '11px', color: '#999' }}>
                        모델: {connectionStatus.local_ai.model}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {loadingConnectionStatus && !connectionStatus && (
            <div style={{ 
              marginBottom: '20px', 
              padding: '12px', 
              backgroundColor: 'white', 
              borderRadius: '6px',
              border: '1px solid #e0e0e0',
              textAlign: 'center',
              color: '#666',
              fontSize: '13px'
            }}>
              AI 연결 상태 확인 중...
            </div>
          )}
          
          {loadingUsageStats && !usageStats ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
              사용량 통계를 불러오는 중...
            </div>
          ) : usageStats ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
              {/* Google AI 사용량 */}
              <div style={{ 
                padding: '15px', 
                backgroundColor: 'white', 
                borderRadius: '6px',
                border: '1px solid #e0e0e0',
                position: 'relative'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontSize: '20px', marginRight: '8px' }}>🔵</span>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>Google AI (유료)</h4>
                  {connectionStatus?.google_ai && (
                    <span style={{ 
                      marginLeft: '8px', 
                      fontSize: '12px',
                      color: connectionStatus.google_ai.connected ? '#28a745' : '#dc3545',
                      fontWeight: 'bold'
                    }}>
                      {connectionStatus.google_ai.connected ? '●' : '○'}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.8' }}>
                  <div>📞 호출 횟수: <strong>{usageStats.google_ai.total_requests.toLocaleString()}</strong>회</div>
                  <div>📥 입력 토큰: <strong>{usageStats.google_ai.total_input_tokens.toLocaleString()}</strong>개</div>
                  <div>📤 출력 토큰: <strong>{usageStats.google_ai.total_output_tokens.toLocaleString()}</strong>개</div>
                  <div>📊 총 토큰: <strong>{usageStats.google_ai.total_tokens.toLocaleString()}</strong>개</div>
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e0e0e0' }}>
                    💰 예상 비용: <strong style={{ color: '#dc3545' }}>${usageStats.google_ai.total_cost.toFixed(4)}</strong>
                    <small style={{ display: 'block', color: '#999', marginTop: '4px', fontSize: '11px' }}>
                      (입력: ${usageStats.google_ai.total_input_tokens > 0 ? (usageStats.google_ai.total_input_tokens * 0.075 / 1000000).toFixed(4) : '0.0000'}, 출력: ${usageStats.google_ai.total_output_tokens > 0 ? (usageStats.google_ai.total_output_tokens * 0.30 / 1000000).toFixed(4) : '0.0000'})
                    </small>
                  </div>
                </div>
              </div>
              
              {/* Local AI 사용량 */}
              <div style={{ 
                padding: '15px', 
                backgroundColor: 'white', 
                borderRadius: '6px',
                border: '1px solid #e0e0e0',
                position: 'relative'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                  <span style={{ fontSize: '20px', marginRight: '8px' }}>🟢</span>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: 'bold' }}>Local AI (무료)</h4>
                  {connectionStatus?.local_ai && (
                    <span style={{ 
                      marginLeft: '8px', 
                      fontSize: '12px',
                      color: connectionStatus.local_ai.connected ? '#28a745' : '#dc3545',
                      fontWeight: 'bold'
                    }}>
                      {connectionStatus.local_ai.connected ? '●' : '○'}
                    </span>
                  )}
                </div>
                <div style={{ fontSize: '14px', color: '#666', lineHeight: '1.8' }}>
                  <div>📞 호출 횟수: <strong>{usageStats.local_ai.total_requests.toLocaleString()}</strong>회</div>
                  <div>📥 입력 토큰: <strong>{usageStats.local_ai.total_input_tokens.toLocaleString()}</strong>개</div>
                  <div>📤 출력 토큰: <strong>{usageStats.local_ai.total_output_tokens.toLocaleString()}</strong>개</div>
                  <div>📊 총 토큰: <strong>{usageStats.local_ai.total_tokens.toLocaleString()}</strong>개</div>
                  <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #e0e0e0' }}>
                    💰 비용: <strong style={{ color: '#28a745' }}>무료</strong>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
              사용량 통계를 불러올 수 없습니다.
            </div>
          )}
          
          <div style={{ marginTop: '15px', padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '4px', fontSize: '12px', color: '#0066cc', lineHeight: '1.6' }}>
            <div style={{ marginBottom: '8px' }}>
              <strong>💡 토큰 이해:</strong>
              <ul style={{ margin: '4px 0 4px 20px', padding: 0 }}>
                <li>토큰은 약 <strong>4자</strong>에 해당합니다</li>
                <li><strong>100개 토큰</strong>은 영어 단어 약 <strong>60~80개</strong>에 해당합니다</li>
                <li>토큰은 단일 문자(예: z) 또는 전체 단어(예: cat)일 수 있습니다</li>
                <li>긴 단어는 여러 토큰으로 나뉩니다</li>
              </ul>
            </div>
            <div>
              <strong>💰 Google AI 가격 (Gemini 2.5 Flash):</strong>
              <ul style={{ margin: '4px 0 0 20px', padding: 0 }}>
                <li>입력: <strong>$0.075</strong> / 1M 토큰</li>
                <li>출력: <strong>$0.30</strong> / 1M 토큰</li>
              </ul>
            </div>
          </div>
            </>
          )}
        </div>
        
        <form onSubmit={handleSubmit}>
          {/* Google AI API 키 */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
              Google AI API 키 {!isAdmin ? '*' : ''}
              {isAdmin && hasEnvKey && (
                <span style={{ 
                  marginLeft: '8px', 
                  fontSize: '12px', 
                  color: '#28a745',
                  fontWeight: 'normal'
                }}>
                  (.env 키 사용 중)
                </span>
              )}
              {!hasEnvKey && hasStoredKey && (
                <span style={{ 
                  marginLeft: '8px', 
                  fontSize: '12px', 
                  color: '#28a745',
                  fontWeight: 'normal'
                }}>
                  (저장된 키 사용 중)
                </span>
              )}
            </label>
            <div style={{ display: 'flex', gap: '10px' }}>
              <input
                type={showApiKey ? 'text' : 'password'}
                value={formData.google_api_key}
                onChange={(e) => setFormData({ ...formData, google_api_key: e.target.value })}
                placeholder={isAdmin && hasEnvKey ? "관리자: .env 키 사용 중 (변경하려면 입력)" : hasStoredKey ? "저장된 키 사용 중 (변경하려면 입력)" : "Google Gemini API 키를 입력하세요"}
                style={{ flex: 1, padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                // required 속성 제거 - JavaScript로 검증
              />
              <button
                type="button"
                onClick={() => setShowApiKey(!showApiKey)}
                style={{ padding: '10px 15px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                {showApiKey ? '🙈' : '👁️'}
              </button>
            </div>
            <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
              Google AI Studio (https://aistudio.google.com)에서 API 키를 발급받을 수 있습니다.
            </small>
          </div>

          {/* AI 거래 활성화 */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                style={{ marginRight: '8px', width: '18px', height: '18px' }}
              />
              <span style={{ fontWeight: 'bold', fontSize: '16px' }}>AI 자동거래 활성화</span>
            </label>
            <small style={{ display: 'block', marginTop: '5px', color: '#666', marginLeft: '26px' }}>
              활성화 시 일반 자동거래 설정이 비활성화되고, AI가 주단위로 분석하여 거래를 결정합니다.
            </small>
          </div>

          {/* AI 자동거래 활성화 시 중단 버튼 */}
          {formData.is_active && (
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <strong style={{ color: '#856404' }}>⚠️ AI 자동거래 활성화 중</strong>
                  <div style={{ fontSize: '13px', color: '#856404', marginTop: '5px' }}>
                    AI가 주단위로 분석하여 거래를 결정하고 있습니다.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleStopClick}
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  설정 중단
                </button>
              </div>
            </div>
          )}

          {formData.is_active && (
            <>
              {/* 투자 사이클 프리셋 */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  주기/사이클 선택
                </label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {[
                    {
                      key: 'ultra_short',
                      label: '초단기(주간)',
                      values: { analysis_period_days: 14, update_frequency_weeks: 1, analysis_depth: 'MEDIUM', investment_strategy: 'short_aggressive' }
                    },
                    {
                      key: 'short_term',
                      label: '단기(월간)',
                      values: { analysis_period_days: 45, update_frequency_weeks: 1, analysis_depth: 'MEDIUM', investment_strategy: 'short_conservative' }
                    },
                    {
                      key: 'mid_term',
                      label: '중기',
                      values: { analysis_period_days: 180, update_frequency_weeks: 2, analysis_depth: 'HIGH', investment_strategy: 'medium_balanced' }
                    },
                    {
                      key: 'long_term',
                      label: '장기',
                      values: { analysis_period_days: 365, update_frequency_weeks: 4, analysis_depth: 'HIGH', investment_strategy: 'long_conservative' }
                    }
                  ].map(preset => (
                    <button
                      key={preset.key}
                      type="button"
                      onClick={() => setFormData({ ...formData, ...preset.values })}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '6px',
                        border: '1px solid #dee2e6',
                        backgroundColor: '#f8f9fa',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                <small style={{ display: 'block', marginTop: '6px', color: '#666' }}>
                  주간/월간 거래 주기에 맞춰 분석 기간·깊이·갱신 주기를 자동 설정합니다.
                </small>
              </div>

              {/* 투자 전략 선택 */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  투자 전략
                </label>
                <select
                  value={formData.investment_strategy}
                  onChange={(e) => setFormData({ ...formData, investment_strategy: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="short_aggressive">초단기 공격형 (주간)</option>
                  <option value="short_conservative">초단기 보수형 (주간)</option>
                  <option value="medium_conservative">단기 보수형 (월간)</option>
                  <option value="medium_balanced">중기 균형형</option>
                  <option value="medium_aggressive">중기 공격형</option>
                  <option value="long_conservative">장기 보수형</option>
                  <option value="long_aggressive">장기 공격형</option>
                  <option value="custom">사용자 정의</option>
                </select>
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  주간·월간 거래에 맞는 초단기/단기/중기/장기 전략을 선택하세요.
                </small>
              </div>

              {/* 분석 기간 */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  분석 기간 (일)
                </label>
                <input
                  type="number"
                  value={formData.analysis_period_days}
                  onChange={(e) => setFormData({ ...formData, analysis_period_days: parseInt(e.target.value) || 365 })}
                  min="30"
                  max="1095"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  분석에 사용할 과거 데이터 기간 (기본: 365일, 최대: 1095일)
                </small>
              </div>

              {/* 분석 깊이 */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  분석 깊이
                </label>
                <select
                  value={formData.analysis_depth}
                  onChange={(e) => setFormData({ ...formData, analysis_depth: e.target.value })}
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                >
                  <option value="LOW">낮음 (빠른 분석)</option>
                  <option value="MEDIUM">보통 (균형잡힌 분석)</option>
                  <option value="HIGH">높음 (심층 분석, 권장)</option>
                </select>
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  분석 깊이가 높을수록 더 정확하지만 시간이 더 걸립니다.
                </small>
              </div>

              {/* 웹 검색 포함 */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.include_web_search}
                    onChange={(e) => setFormData({ ...formData, include_web_search: e.target.checked })}
                    style={{ marginRight: '8px', width: '18px', height: '18px' }}
                  />
                  <span>웹 검색 포함 (최신 뉴스 및 시장 동향 반영)</span>
                </label>
              </div>

              {/* 기업 자료 포함 */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={formData.include_company_data}
                    onChange={(e) => setFormData({ ...formData, include_company_data: e.target.checked })}
                    style={{ marginRight: '8px', width: '18px', height: '18px' }}
                  />
                  <span>기업 자료 포함 (재무제표, 실적, 공시 등)</span>
                </label>
              </div>

              {/* 갱신 주기 */}
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                  갱신 주기 (주)
                </label>
                <input
                  type="number"
                  value={formData.update_frequency_weeks}
                  onChange={(e) => setFormData({ ...formData, update_frequency_weeks: parseInt(e.target.value) || 1 })}
                  min="1"
                  max="4"
                  style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '4px' }}
                />
                <small style={{ display: 'block', marginTop: '5px', color: '#666' }}>
                  AI 분석 및 예약 주문 갱신 주기 (기본: 1주, 최대: 4주)
                </small>
              </div>

              {/* 자동 거래 설정 섹션 */}
              <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showAutoTradeSettings ? '20px' : '0' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', color: '#333' }}>
                    🤖 자동 거래 실행 설정
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowAutoTradeSettings(!showAutoTradeSettings)}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                    title={showAutoTradeSettings ? '박스 숨기기' : '박스 보이기'}
                  >
                    {showAutoTradeSettings ? '👁️ 감추기' : '👁️ 보이기'}
                  </button>
                </div>
                
                {showAutoTradeSettings && (
                  <>

                {/* 즉시 실행 옵션 */}
                <div style={{ marginBottom: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={formData.execute_immediately}
                      onChange={(e) => setFormData({ ...formData, execute_immediately: e.target.checked })}
                      style={{ marginRight: '8px', width: '18px', height: '18px' }}
                    />
                    <span style={{ fontWeight: 'bold', fontSize: '15px' }}>AI 분석 후 즉시 실행</span>
                  </label>
                  <small style={{ display: 'block', marginTop: '5px', color: '#666', marginLeft: '26px' }}>
                    활성화 시 AI 분석 완료 후 조건 충족 시 즉시 매수/매도 주문을 실행합니다. (확신도 임계값 이상일 때만 실행)
                  </small>
                </div>

                {/* 실행 스케줄 규칙 */}
                <div style={{ marginBottom: '25px', padding: '15px', backgroundColor: '#fdfdfd', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h4 style={{ marginTop: 0, marginBottom: '10px', fontSize: '15px' }}>⏱️ 실행 스케줄</h4>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                    <label style={{ fontSize: '13px', fontWeight: '500' }}>
                      모드
                      <select
                        value={formData.schedule_mode}
                        onChange={(e) => setFormData({ ...formData, schedule_mode: e.target.value })}
                        style={{ marginLeft: '6px', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                      >
                        <option value="immediate">즉시</option>
                        <option value="delay">지연 실행</option>
                        <option value="window">시간대 실행</option>
                      </select>
                    </label>
                    {formData.schedule_mode === 'delay' && (
                      <label style={{ fontSize: '13px', fontWeight: '500' }}>
                        지연 (분)
                        <input
                          type="number"
                          min="0"
                          value={formData.schedule_delay_minutes}
                          onChange={(e) => setFormData({ ...formData, schedule_delay_minutes: parseInt(e.target.value, 10) || 0 })}
                          style={{ marginLeft: '6px', padding: '6px', borderRadius: '4px', border: '1px solid #ddd', width: '80px' }}
                        />
                      </label>
                    )}
                  </div>

                  {formData.schedule_mode === 'window' && (
                    <>
                      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
                        <label style={{ fontSize: '13px', fontWeight: '500' }}>
                          시작 시간
                          <input
                            type="time"
                            value={formData.schedule_window_start}
                            onChange={(e) => setFormData({ ...formData, schedule_window_start: e.target.value })}
                            style={{ marginLeft: '6px', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                          />
                        </label>
                        <label style={{ fontSize: '13px', fontWeight: '500' }}>
                          종료 시간
                          <input
                            type="time"
                            value={formData.schedule_window_end}
                            onChange={(e) => setFormData({ ...formData, schedule_window_end: e.target.value })}
                            style={{ marginLeft: '6px', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
                          />
                        </label>
                        <label style={{ display: 'flex', alignItems: 'center', fontSize: '13px', fontWeight: '500', gap: '6px' }}>
                          <input
                            type="checkbox"
                            checked={formData.schedule_market_hours}
                            onChange={(e) => setFormData({ ...formData, schedule_market_hours: e.target.checked })}
                          />
                          장중 시간만 허용
                        </label>
                      </div>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {[1, 2, 3, 4, 5].map(day => (
                          <label key={day} style={{ fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <input
                              type="checkbox"
                              checked={formData.schedule_weekdays.includes(day)}
                              onChange={(e) => {
                                const next = e.target.checked
                                  ? [...formData.schedule_weekdays, day]
                                  : formData.schedule_weekdays.filter(item => item !== day);
                                setFormData({ ...formData, schedule_weekdays: next });
                              }}
                            />
                            {['월', '화', '수', '목', '금'][day - 1]}
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                  <small style={{ display: 'block', marginTop: '8px', color: '#666', fontSize: '12px' }}>
                    시간대 실행은 선택된 요일/시간대에 예약 주문으로 전환됩니다.
                  </small>
                </div>

                {/* 자동 매수 설정 */}
                <div style={{ marginBottom: '25px', padding: '15px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={formData.auto_buy_enabled}
                        onChange={(e) => setFormData({ ...formData, auto_buy_enabled: e.target.checked })}
                        style={{ marginRight: '8px', width: '18px', height: '18px' }}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '15px' }}>자동 매수 활성화</span>
                    </label>
                  </div>
                  
                  {formData.auto_buy_enabled && (
                    <div style={{ marginLeft: '26px' }}>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                          확신도 임계값 (%)
                        </label>
                        <input
                          type="number"
                          value={formData.auto_buy_confidence_threshold}
                          onChange={(e) => setFormData({ ...formData, auto_buy_confidence_threshold: parseInt(e.target.value) || 70 })}
                          min="0"
                          max="100"
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                          이 확신도 이상일 때만 자동 매수 실행 (기본: 70%)
                        </small>
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                          매수 비율 (%)
                        </label>
                        <input
                          type="number"
                          value={formData.auto_buy_quantity_percent}
                          onChange={(e) => setFormData({ ...formData, auto_buy_quantity_percent: parseInt(e.target.value) || 10 })}
                          min="1"
                          max="100"
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                          주문가능현금 대비 매수 비율 (기본: 10%)
                        </small>
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                          일일 매수 한도 (건)
                        </label>
                        <input
                          type="number"
                          value={formData.auto_buy_max_daily_purchases}
                          onChange={(e) => setFormData({ ...formData, auto_buy_max_daily_purchases: parseInt(e.target.value) || 5 })}
                          min="1"
                          max="20"
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                          하루 최대 자동 매수 건수 (기본: 5). 거래량 조절에 사용됩니다.
                        </small>
                      </div>
                    </div>
                  )}
                </div>

                {/* 자동 매도 설정 */}
                <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={formData.auto_sell_enabled}
                        onChange={(e) => setFormData({ ...formData, auto_sell_enabled: e.target.checked })}
                        style={{ marginRight: '8px', width: '18px', height: '18px' }}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '15px' }}>자동 매도 활성화</span>
                    </label>
                  </div>
                  
                  {formData.auto_sell_enabled && (
                    <div style={{ marginLeft: '26px' }}>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                          확신도 임계값 (%)
                        </label>
                        <input
                          type="number"
                          value={formData.auto_sell_confidence_threshold}
                          onChange={(e) => setFormData({ ...formData, auto_sell_confidence_threshold: parseInt(e.target.value) || 70 })}
                          min="0"
                          max="100"
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                          이 확신도 이상일 때만 자동 매도 실행 (기본: 70%)
                        </small>
                      </div>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                          매도 비율 (%)
                        </label>
                        <input
                          type="number"
                          value={formData.auto_sell_quantity_percent}
                          onChange={(e) => setFormData({ ...formData, auto_sell_quantity_percent: parseInt(e.target.value) || 50 })}
                          min="1"
                          max="100"
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                          보유수량 대비 매도 비율 (기본: 50%)
                        </small>
                      </div>
                    </div>
                  )}
                </div>

                {/* 손실 트리거 설정 (손실 관리 강화 권장) */}
                <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: '#fff3cd', borderRadius: '6px', border: '1px solid #ffc107' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', flex: 1 }}>
                      <input
                        type="checkbox"
                        checked={formData.loss_trigger_enabled}
                        onChange={(e) => setFormData({ ...formData, loss_trigger_enabled: e.target.checked })}
                        style={{ marginRight: '8px', width: '18px', height: '18px' }}
                      />
                      <span style={{ fontWeight: 'bold', fontSize: '15px' }}>손실 트리거 활성화</span>
                      <span style={{ marginLeft: '8px', fontSize: '12px', color: '#856404' }}>(손실 관리 권장)</span>
                    </label>
                  </div>
                  
                  {formData.loss_trigger_enabled && (
                    <div style={{ marginLeft: '26px' }}>
                      <div style={{ marginBottom: '12px', padding: '10px', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                        <small style={{ display: 'block', marginBottom: '8px', color: '#856404', fontSize: '12px', lineHeight: '1.5' }}>
                          💡 손실 트리거: 설정한 손실률 또는 손실액에 도달하면 자동으로 매도합니다. (주기적으로 체크)<br />
                          <strong>손실 관리를 위해 활성화를 권장합니다.</strong> 미활성화 시 손실이 커질 수 있습니다.
                        </small>
                      </div>
                      
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                          손실률 임계값 (%)
                        </label>
                        <input
                          type="number"
                          value={formData.loss_trigger_percent_threshold}
                          onChange={(e) => setFormData({ ...formData, loss_trigger_percent_threshold: parseFloat(e.target.value) || 3 })}
                          min="0"
                          step="0.1"
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                          이 손실률 이상일 때 트리거 (예: 3 → -3% 손실 시 트리거, 기본: 3%)
                        </small>
                      </div>
                      
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                          손실액 임계값 (원)
                        </label>
                        <input
                          type="number"
                          value={formData.loss_trigger_amount_threshold}
                          onChange={(e) => setFormData({ ...formData, loss_trigger_amount_threshold: parseInt(e.target.value) || 0 })}
                          min="0"
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                          이 손실액 이상일 때 트리거 (0이면 사용 안 함, 기본: 0원)
                        </small>
                      </div>
                      
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                          매도 비율 (%)
                        </label>
                        <input
                          type="number"
                          value={formData.loss_trigger_sell_percentage}
                          onChange={(e) => setFormData({ ...formData, loss_trigger_sell_percentage: parseInt(e.target.value) || 50 })}
                          min="1"
                          max="100"
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                          트리거 발생 시 매도할 보유 비율 (기본: 50%)
                        </small>
                      </div>
                      
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                          주문 방법
                        </label>
                        <select
                          value={formData.loss_trigger_order_method}
                          onChange={(e) => setFormData({ ...formData, loss_trigger_order_method: e.target.value })}
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        >
                          <option value="market">시장가</option>
                          <option value="limit">지정가</option>
                        </select>
                        <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                          매도 주문 방법 (기본: 시장가)
                        </small>
                      </div>
                      
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                          최소 보유 기간 (일)
                        </label>
                        <input
                          type="number"
                          value={formData.loss_trigger_min_holding_days}
                          onChange={(e) => setFormData({ ...formData, loss_trigger_min_holding_days: parseInt(e.target.value) || 0 })}
                          min="0"
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                          이 기간 미만이면 매도하지 않음 (0이면 제한 없음, 기본: 0일)
                        </small>
                      </div>
                      
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                          실행 방식
                        </label>
                        <select
                          value={formData.loss_trigger_action}
                          onChange={(e) => setFormData({ ...formData, loss_trigger_action: e.target.value })}
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        >
                          <option value="force">강제 매도 (즉시 실행)</option>
                          <option value="recommend">권고 매도 (알림만)</option>
                        </select>
                        <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                          force: 즉시 매도 시도, recommend: 예약 주문 생성 및 알림 (기본: force)
                        </small>
                      </div>
                      
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                          체크 주기 (분)
                        </label>
                        <input
                          type="number"
                          value={formData.loss_trigger_check_interval_minutes}
                          onChange={(e) => setFormData({ ...formData, loss_trigger_check_interval_minutes: parseInt(e.target.value) || 15 })}
                          min="1"
                          style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                        />
                        <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                          손실 트리거 체크 주기 (기본: 15분, 서버에서 5분마다 실행되지만 사용자별 체크 주기 적용)
                        </small>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ padding: '12px', backgroundColor: '#e7f3ff', borderRadius: '6px', fontSize: '13px', color: '#004085' }}>
                  <strong>💡 참고:</strong> 자동 거래는 AI 분석 완료 후 조건이 충족될 때만 실행됩니다.
                  <br />
                  • 즉시 실행 옵션: 분석 완료 즉시 조건 충족 시 주문 실행
                  <br />
                  • 비활성화 시: 예약 주문만 생성 (수동 확인 후 실행)
                  <br />
                  • 손실 트리거: 설정한 손실률/손실액 도달 시 자동 매도 (5분마다 체크)
                </div>
                  </>
                )}
              </div>

              {/* 잔고 및 포트폴리오 제한 설정 섹션 */}
              <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '1px solid #ffc107' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showBalanceLimits ? '20px' : '0' }}>
                  <h3 style={{ margin: 0, fontSize: '18px', color: '#856404' }}>
                    💰 잔고 및 포트폴리오 제한 설정
                  </h3>
                  <button
                    type="button"
                    onClick={() => setShowBalanceLimits(!showBalanceLimits)}
                    style={{
                      padding: '5px 10px',
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                    title={showBalanceLimits ? '박스 숨기기' : '박스 보이기'}
                  >
                    {showBalanceLimits ? '👁️ 감추기' : '👁️ 보이기'}
                  </button>
                </div>
                
                {showBalanceLimits && (
                  <>

                {/* 잔고 제한 설정 */}
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h4 style={{ marginTop: 0, marginBottom: '15px', fontSize: '16px', color: '#333' }}>잔고 제한</h4>
                  
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      최대 잔고 사용 비율 (%)
                    </label>
                    <input
                      type="number"
                      value={formData.max_balance_usage_percent}
                      onChange={(e) => setFormData({ ...formData, max_balance_usage_percent: parseInt(e.target.value) || 100 })}
                      min="0"
                      max="100"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                      주문가능현금 중 사용 가능한 최대 비율 (기본: 100%)
                    </small>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      예약 잔고 (원)
                    </label>
                    <input
                      type="number"
                      value={formData.reserved_balance}
                      onChange={(e) => setFormData({ ...formData, reserved_balance: parseInt(e.target.value) || 0 })}
                      min="0"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                      AI 거래에서 사용하지 않고 유지할 최소 잔고 (기본: 0원)
                    </small>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      지정 현금 잔고 (원)
                    </label>
                    <input
                      type="number"
                      value={formData.designated_cash_balance}
                      onChange={(e) => setFormData({ ...formData, designated_cash_balance: parseInt(e.target.value) || 0 })}
                      min="0"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                      특정 목적을 위해 별도로 유지할 현금 잔고 (기본: 0원)
                    </small>
                  </div>
                </div>

                {/* 포트폴리오 제한 설정 */}
                <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h4 style={{ marginTop: 0, marginBottom: '15px', fontSize: '16px', color: '#333' }}>포트폴리오 제한</h4>
                  
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      종목별 최대 보유 비율 (%)
                    </label>
                    <input
                      type="number"
                      value={formData.max_holding_percent}
                      onChange={(e) => setFormData({ ...formData, max_holding_percent: parseInt(e.target.value) || 20 })}
                      min="1"
                      max="100"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                      포트폴리오에서 단일 종목이 차지할 수 있는 최대 비율 (기본: 20%)
                    </small>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      최대 보유 종목 수 (개)
                    </label>
                    <input
                      type="number"
                      value={formData.max_holdings_count}
                      onChange={(e) => setFormData({ ...formData, max_holdings_count: parseInt(e.target.value) || 20 })}
                      min="1"
                      max="100"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                      포트폴리오에 보유할 수 있는 최대 종목 수 (기본: 20개)
                    </small>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      최소거래비용 (원)
                    </label>
                    <input
                      type="number"
                      value={formData.min_investment_amount ?? 100000}
                      onChange={(e) => setFormData({ ...formData, min_investment_amount: parseInt(e.target.value) || 100000 })}
                      min="0"
                      placeholder="100000"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                      매수 시 최소 주문 금액, 매도 시 이 금액 이하 보유는 비율 적용 없이 전량 매도 (기본: 10만원)
                    </small>
                  </div>
                </div>

                {/* 가격 변동률 설정 */}
                <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h4 style={{ marginTop: 0, marginBottom: '15px', fontSize: '16px', color: '#333' }}>가격 변동률 제한</h4>
                  
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      최대 가격 변동률 허용 범위 (%)
                    </label>
                    <input
                      type="number"
                      value={formData.max_price_change_percent}
                      onChange={(e) => setFormData({ ...formData, max_price_change_percent: parseFloat(e.target.value) || 10 })}
                      min="1"
                      max="50"
                      step="0.1"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                      분석 시점 가격 대비 현재가 변동률이 이 값을 초과하면 주문 취소 또는 가격 조정 (기본: 10%)
                    </small>
                  </div>

                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={formData.adjust_price_on_volatility}
                        onChange={(e) => setFormData({ ...formData, adjust_price_on_volatility: e.target.checked })}
                        style={{ marginRight: '10px', width: '18px', height: '18px' }}
                      />
                      <span style={{ fontSize: '14px', fontWeight: '500' }}>
                        가격 변동률 초과 시 현재가로 자동 조정
                      </span>
                    </label>
                    <small style={{ display: 'block', marginTop: '3px', marginLeft: '28px', color: '#666', fontSize: '12px' }}>
                      활성화 시 가격 변동률이 초과해도 현재가로 주문 실행 (기본: 활성화)
                    </small>
                  </div>
                </div>

                <div style={{ padding: '12px', backgroundColor: '#fff', borderRadius: '6px', fontSize: '13px', color: '#856404', border: '1px solid #ffc107' }}>
                  <strong>💡 계산 공식:</strong>
                  <br />
                  사용 가능한 잔고 = (주문가능현금 × 최대사용비율 / 100) - 예약잔고 - 지정현금잔고
                  <br />
                  <strong>💡 가격 변동률:</strong>
                  <br />
                  가격 변동률 = ((현재가 - 분석시점가격) / 분석시점가격) × 100
                  <br />
                  변동률이 임계값을 초과하면 {formData.adjust_price_on_volatility ? '현재가로 조정하여 주문' : '주문 취소'}
                </div>
                  </>
                )}
              </div>
            </>
          )}

          {/* 사용자 투자 프로필 정보 */}
          <div style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '6px', border: '1px solid #dee2e6' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h4 style={{ margin: 0, fontSize: '16px', color: '#333' }}>
                👤 사용자 투자 프로필 정보
              </h4>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showUserProfile}
                  onChange={(e) => {
                    setShowUserProfile(e.target.checked);
                    setFormData({ ...formData, profile_visible: e.target.checked });
                  }}
                  style={{ marginRight: '5px' }}
                />
                <span style={{ fontSize: '13px', color: '#666' }}>보이기</span>
              </label>
            </div>
            <small style={{ display: 'block', marginBottom: '15px', color: '#666', fontSize: '12px' }}>
              AI가 종목 추천 및 분석 시 고려할 사용자 정보입니다. 설정하지 않으면 일반적인 추천을 제공합니다.
            </small>

            {showUserProfile && (
              <>
                {/* 투자성향 */}
                <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h5 style={{ marginTop: 0, marginBottom: '12px', fontSize: '15px', color: '#333' }}>투자성향</h5>
                  <select
                    value={formData.risk_tolerance}
                    onChange={(e) => setFormData({ ...formData, risk_tolerance: e.target.value })}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="">선택 안 함</option>
                    <option value="conservative">보수적 (안정성 우선)</option>
                    <option value="moderate">중립 (균형형)</option>
                    <option value="aggressive">공격적 (수익 추구)</option>
                  </select>
                </div>

                {/* 재정상태 */}
                <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h5 style={{ marginTop: 0, marginBottom: '12px', fontSize: '15px', color: '#333' }}>재정상태</h5>
                  
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      월 소득 (만원)
                    </label>
                    <input
                      type="number"
                      value={formData.monthly_income}
                      onChange={(e) => setFormData({ ...formData, monthly_income: parseInt(e.target.value) || 0 })}
                      min="0"
                      placeholder="예: 500"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>

                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      투자 가능 자산 (만원)
                    </label>
                    <input
                      type="number"
                      value={formData.investable_assets}
                      onChange={(e) => setFormData({ ...formData, investable_assets: parseInt(e.target.value) || 0 })}
                      min="0"
                      placeholder="예: 10000"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      투자 경력 (년)
                    </label>
                    <input
                      type="number"
                      value={formData.investment_experience_years}
                      onChange={(e) => setFormData({ ...formData, investment_experience_years: parseInt(e.target.value) || 0 })}
                      min="0"
                      placeholder="예: 5"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>
                </div>

                {/* 투자 목표 */}
                <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h5 style={{ marginTop: 0, marginBottom: '12px', fontSize: '15px', color: '#333' }}>투자 목표</h5>
                  
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      주요 목표
                    </label>
                    <select
                      value={formData.primary_goal}
                      onChange={(e) => setFormData({ ...formData, primary_goal: e.target.value })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <option value="">선택 안 함</option>
                      <option value="capital_growth">자본 성장</option>
                      <option value="income">수익 창출</option>
                      <option value="preservation">자산 보존</option>
                      <option value="speculation">투기</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      투자 기간
                    </label>
                    <select
                      value={formData.time_horizon}
                      onChange={(e) => setFormData({ ...formData, time_horizon: e.target.value })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <option value="">선택 안 함</option>
                      <option value="short_term">단기 (1년 이하)</option>
                      <option value="medium_term">중기 (1-5년)</option>
                      <option value="long_term">장기 (5년 이상)</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      목표 수익률 (%)
                    </label>
                    <input
                      type="number"
                      value={formData.target_return_percent}
                      onChange={(e) => setFormData({ ...formData, target_return_percent: parseInt(e.target.value) || 0 })}
                      min="0"
                      max="100"
                      placeholder="예: 10"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                  </div>

                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      수익목표 달성 시 매도 방식
                    </label>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="profit_target_sell_mode"
                          value="immediate"
                          checked={formData.profit_target_sell_mode === 'immediate'}
                          onChange={(e) => setFormData({ ...formData, profit_target_sell_mode: e.target.value })}
                        />
                        즉시 매도
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="profit_target_sell_mode"
                          value="on_downturn"
                          checked={formData.profit_target_sell_mode === 'on_downturn'}
                          onChange={(e) => setFormData({ ...formData, profit_target_sell_mode: e.target.value })}
                        />
                        하향 추세 전환 시 매도
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer' }}>
                        <input
                          type="radio"
                          name="profit_target_sell_mode"
                          value="hold"
                          checked={formData.profit_target_sell_mode === 'hold'}
                          onChange={(e) => setFormData({ ...formData, profit_target_sell_mode: e.target.value })}
                        />
                        대기(사용자 직접 결정)
                      </label>
                    </div>
                    {formData.profit_target_sell_mode === 'on_downturn' && (
                      <div style={{ marginTop: '10px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                        <div style={{ flex: '1 1 120px' }}>
                          <label style={{ fontSize: '12px', color: '#666' }}>추세 판단 일수</label>
                          <input
                            type="number"
                            value={formData.profit_target_downturn_lookback_days}
                            onChange={(e) => setFormData({ ...formData, profit_target_downturn_lookback_days: parseInt(e.target.value) || 5 })}
                            min="1"
                            max="20"
                            style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '2px' }}
                          />
                        </div>
                        <div style={{ flex: '1 1 120px' }}>
                          <label style={{ fontSize: '12px', color: '#666' }}>고점 대비 하락률 (%)</label>
                          <input
                            type="number"
                            value={formData.profit_target_downturn_threshold_percent}
                            onChange={(e) => setFormData({ ...formData, profit_target_downturn_threshold_percent: parseFloat(e.target.value) || 2 })}
                            min="0.5"
                            max="20"
                            step="0.5"
                            style={{ width: '100%', padding: '6px', border: '1px solid #ddd', borderRadius: '4px', marginTop: '2px' }}
                          />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* 선호사항 */}
                <div style={{ marginBottom: '15px', padding: '15px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #dee2e6' }}>
                  <h5 style={{ marginTop: 0, marginBottom: '12px', fontSize: '15px', color: '#333' }}>선호사항</h5>
                  
                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      선호 업종 (쉼표로 구분)
                    </label>
                    <input
                      type="text"
                      value={preferredSectorsInput}
                      onChange={(e) => {
                        setPreferredSectorsInput(e.target.value);
                        // 실시간으로 배열로 변환하여 formData 업데이트
                        const sectors = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                        setFormData({ ...formData, preferred_sectors: sectors });
                      }}
                      onBlur={(e) => {
                        // blur 시 최종 정리 (중복 제거, 정렬)
                        const sectors = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                        const uniqueSectors = [...new Set(sectors)];
                        setPreferredSectorsInput(uniqueSectors.join(', '));
                        setFormData({ ...formData, preferred_sectors: uniqueSectors });
                      }}
                      placeholder="예: IT, 바이오, 금융"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                      AI가 우선적으로 추천할 업종 (쉼표로 구분하여 입력)
                    </small>
                  </div>

                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      제외 업종 (쉼표로 구분)
                    </label>
                    <input
                      type="text"
                      value={excludedSectorsInput}
                      onChange={(e) => {
                        setExcludedSectorsInput(e.target.value);
                        // 실시간으로 배열로 변환하여 formData 업데이트
                        const sectors = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                        setFormData({ ...formData, excluded_sectors: sectors });
                      }}
                      onBlur={(e) => {
                        // blur 시 최종 정리 (중복 제거, 정렬)
                        const sectors = e.target.value.split(',').map(s => s.trim()).filter(s => s);
                        const uniqueSectors = [...new Set(sectors)];
                        setExcludedSectorsInput(uniqueSectors.join(', '));
                        setFormData({ ...formData, excluded_sectors: uniqueSectors });
                      }}
                      placeholder="예: 석유, 담배"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                      AI가 추천하지 않을 업종 (쉼표로 구분하여 입력)
                    </small>
                  </div>

                  <div style={{ marginBottom: '10px' }}>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      선호 시가총액
                    </label>
                    <select
                      value={formData.preferred_market_cap}
                      onChange={(e) => setFormData({ ...formData, preferred_market_cap: e.target.value })}
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    >
                      <option value="">전체</option>
                      <option value="large">대형주</option>
                      <option value="mid">중형주</option>
                      <option value="small">소형주</option>
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                      최대 포지션 크기 (%)
                    </label>
                    <input
                      type="number"
                      value={formData.max_position_size_percent}
                      onChange={(e) => setFormData({ ...formData, max_position_size_percent: parseInt(e.target.value) || 20 })}
                      min="1"
                      max="100"
                      style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                    <small style={{ display: 'block', marginTop: '3px', color: '#666', fontSize: '12px' }}>
                      단일 종목에 투자할 수 있는 최대 비율 (기본: 20%)
                    </small>
                  </div>
                </div>
              </>
            )}
          </div>

          {/* 포트폴리오 분석 및 AI 추천 종목 분석 */}
          <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#e7f3ff', borderRadius: '8px', border: '1px solid #b3d9ff' }}>
            <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '18px', color: '#004085' }}>
              📊 포트폴리오 분석 및 AI 추천 종목
            </h3>

            {/* 탭 메뉴 (모바일에서 아이콘만 노출) */}
            <div className="ai-trade-config-tabs" style={{ display: 'flex', gap: '10px', marginBottom: '20px', borderBottom: '2px solid #b3d9ff' }}>
              <button
                type="button"
                onClick={() => setActiveAnalysisTab('portfolio')}
                title="포트폴리오 분석"
                style={{
                  padding: '10px 20px',
                  backgroundColor: activeAnalysisTab === 'portfolio' ? '#004085' : 'transparent',
                  color: activeAnalysisTab === 'portfolio' ? 'white' : '#004085',
                  border: 'none',
                  borderBottom: activeAnalysisTab === 'portfolio' ? '3px solid #004085' : '3px solid transparent',
                  cursor: 'pointer',
                  fontWeight: activeAnalysisTab === 'portfolio' ? 'bold' : 'normal'
                }}
              >
                <span className="ai-trade-config-tab-icon" aria-hidden>📊</span>
                <span className="ai-trade-config-tab-label">포트폴리오 분석</span>
              </button>
              <button
                type="button"
                onClick={() => setActiveAnalysisTab('recommended')}
                title="AI 추천 종목"
                style={{
                  padding: '10px 20px',
                  backgroundColor: activeAnalysisTab === 'recommended' ? '#004085' : 'transparent',
                  color: activeAnalysisTab === 'recommended' ? 'white' : '#004085',
                  border: 'none',
                  borderBottom: activeAnalysisTab === 'recommended' ? '3px solid #004085' : '3px solid transparent',
                  cursor: 'pointer',
                  fontWeight: activeAnalysisTab === 'recommended' ? 'bold' : 'normal'
                }}
              >
                <span className="ai-trade-config-tab-icon" aria-hidden>⭐</span>
                <span className="ai-trade-config-tab-label">AI 추천 종목</span>
              </button>
            </div>

            {/* 분석 결과 목록 */}
            {loadingAnalyses ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>로딩 중...</div>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                {(activeAnalysisTab === 'portfolio' ? portfolioAnalyses : recommendedAnalyses).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                    {activeAnalysisTab === 'portfolio' 
                      ? '포트폴리오 분석 결과가 없습니다. 포트폴리오 분석을 실행해주세요.'
                      : 'AI 추천 종목이 없습니다. AI 종목 추천을 실행해주세요.'}
                  </div>
                ) : (
                  (activeAnalysisTab === 'portfolio' ? portfolioAnalyses : recommendedAnalyses).map((analysis) => (
                    <div
                      key={analysis._id}
                      style={{
                        marginBottom: '15px',
                        padding: '15px',
                        backgroundColor: 'white',
                        borderRadius: '6px',
                        border: '1px solid #dee2e6',
                        cursor: 'pointer'
                      }}
                      onClick={() => {
                        setSelectedAnalysis(analysis);
                        setShowAnalysisDetail(true);
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '10px' }}>
                        <div>
                          <strong style={{ fontSize: '16px' }}>{analysis.stock_name || analysis.symbol}</strong>
                          <span style={{ marginLeft: '10px', fontSize: '14px', color: '#666' }}>({analysis.symbol})</span>
                        </div>
                        <span style={{
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          backgroundColor: analysis.current_status === '보유중' ? '#d4edda' : 
                                          analysis.current_status === '구매예정' ? '#d1ecf1' : 
                                          analysis.current_status === '매도예정' ? '#f8d7da' : '#e2e3e5',
                          color: analysis.current_status === '보유중' ? '#155724' : 
                                 analysis.current_status === '구매예정' ? '#0c5460' : 
                                 analysis.current_status === '매도예정' ? '#721c24' : '#383d41'
                        }}>
                          {analysis.current_status}
                        </span>
                      </div>

                      {analysis.recommendation && (
                        <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
                          <div style={{ fontSize: '14px' }}>
                            <strong>AI 추천:</strong> {
                              analysis.recommendation.action === 'buy' ? '매수' :
                              analysis.recommendation.action === 'sell' ? '매도' :
                              analysis.recommendation.action === 'hold' ? '보유' : '없음'
                            }
                            {analysis.recommendation.confidence > 0 && (
                              <span style={{ marginLeft: '10px', color: '#28a745' }}>
                                (신뢰도: {analysis.recommendation.confidence}%)
                              </span>
                            )}
                          </div>
                          {analysis.recommendation.target_price > 0 && (
                            <div style={{ fontSize: '13px', color: '#666', marginTop: '5px' }}>
                              목표가: ₩{analysis.recommendation.target_price.toLocaleString()}
                            </div>
                          )}
                        </div>
                      )}

                      {analysis.trade_config && analysis.trade_config.order_type !== 'none' && (
                        <div style={{ fontSize: '13px', color: '#666' }}>
                          <strong>거래 계획:</strong> {
                            analysis.trade_config.order_type === 'buy' ? '매수' :
                            analysis.trade_config.order_type === 'sell' ? '매도' : '없음'
                          }
                          {analysis.trade_config.quantity > 0 && (
                            <span> {analysis.trade_config.quantity}주</span>
                          )}
                          {analysis.trade_config.price > 0 && (
                            <span> @ ₩{analysis.trade_config.price.toLocaleString()}</span>
                          )}
                        </div>
                      )}

                      <div style={{ marginTop: '10px', fontSize: '12px', color: '#999' }}>
                        {analysis.summary && analysis.summary.substring(0, 100)}
                        {analysis.summary && analysis.summary.length > 100 && '...'}
                      </div>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* 거래 계획 수정 모달 */}
          {selectedAnalysis && editingPlan && (
            <div
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.7)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                zIndex: 10000,
                paddingTop: '80px', // 헤더 높이만큼 상단 여백 추가
                padding: '20px',
                boxSizing: 'border-box'
              }}
              onClick={() => {
                setSelectedAnalysis(null);
                setEditingPlan(null);
              }}
            >
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '10px',
                  padding: '30px',
                  maxWidth: '600px',
                  width: '90%',
                  maxHeight: 'calc(100vh - 120px)', // 헤더 높이 + 여백 고려하여 조정
                  overflow: 'auto',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                  position: 'relative',
                  marginTop: 'auto',
                  marginBottom: 'auto'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <h3 style={{ marginTop: 0 }}>📝 AI 거래 계획 수정</h3>
                <p style={{ marginBottom: '20px', color: '#666' }}>
                  <strong>{selectedAnalysis.stock_name || selectedAnalysis.symbol}</strong> ({selectedAnalysis.symbol})
                </p>

                <div style={{ marginBottom: '15px' }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    거래 유형
                  </label>
                  <select
                    value={editingPlan.order_type}
                    onChange={(e) => setEditingPlan({ ...editingPlan, order_type: e.target.value })}
                    style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                  >
                    <option value="none">없음</option>
                    <option value="buy">매수</option>
                    <option value="sell">매도</option>
                    <option value="hold">보유</option>
                  </select>
                </div>

                {editingPlan.order_type !== 'none' && editingPlan.order_type !== 'hold' && (
                  <>
                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        수량
                      </label>
                      <input
                        type="number"
                        value={editingPlan.quantity}
                        onChange={(e) => setEditingPlan({ ...editingPlan, quantity: parseInt(e.target.value) || 0 })}
                        min="1"
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        가격 (원)
                      </label>
                      <input
                        type="number"
                        value={editingPlan.price}
                        onChange={(e) => setEditingPlan({ ...editingPlan, price: parseInt(e.target.value) || 0 })}
                        min="0"
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      />
                    </div>

                    <div style={{ marginBottom: '15px' }}>
                      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                        주문 방법
                      </label>
                      <select
                        value={editingPlan.order_method}
                        onChange={(e) => setEditingPlan({ ...editingPlan, order_method: e.target.value })}
                        style={{ width: '100%', padding: '8px', border: '1px solid #ddd', borderRadius: '4px' }}
                      >
                        <option value="limit">지정가</option>
                        <option value="market">시장가</option>
                      </select>
                    </div>
                  </>
                )}

                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedAnalysis(null);
                      setEditingPlan(null);
                    }}
                    style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    취소
                  </button>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        // analysisId가 있으면 사용, 없으면 selectedAnalysis._id 사용
                        const analysisId = editingPlan.analysisId || selectedAnalysis?._id;
                        if (!analysisId) {
                          alert('분석 ID를 찾을 수 없습니다.');
                          return;
                        }
                        await updateTradePlan(analysisId, editingPlan);
                        setShowAnalysisDetail(false);
                        setEditingPlan(null);
                      } catch (error) {
                        console.error('계획 수정 실패:', error);
                      }
                    }}
                    style={{ padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    저장
                  </button>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '30px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{ padding: '10px 20px', backgroundColor: '#6c757d', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              취소
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{ padding: '10px 20px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
            >
              {loading ? '저장 중...' : '저장'}
            </button>
          </div>
        </form>

        {/* 중단 확인 모달 */}
        {showStopConfirm && (
          <div
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0,0,0,0.7)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 10000,
              paddingTop: '80px', // 헤더 높이만큼 상단 여백 추가
              padding: '20px',
              boxSizing: 'border-box'
            }}
            onClick={() => setShowStopConfirm(false)}
          >
            <div
              style={{
                backgroundColor: 'white',
                borderRadius: '10px',
                padding: '30px',
                maxWidth: '800px',
                width: '90%',
                maxHeight: 'calc(100vh - 120px)', // 헤더 높이 + 여백 고려하여 조정
                overflow: 'auto',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                position: 'relative',
                marginTop: 'auto',
                marginBottom: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h2 style={{ marginTop: 0, color: '#dc3545' }}>⚠️ AI 자동거래 중단 확인</h2>
              
              <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <p style={{ margin: '0 0 10px 0', fontWeight: '600' }}>
                  AI 자동거래를 중단하시겠습니까?
                </p>
                <p style={{ margin: 0, fontSize: '14px', color: '#666' }}>
                  중단 시 AI가 더 이상 자동으로 거래를 실행하지 않으며, 일반 자동거래 설정이 다시 활성화됩니다.
                </p>
              </div>

              {/* 거래 내역 통계 */}
              {tradeStats && (
                <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e7f3ff', borderRadius: '8px' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '16px' }}>📊 AI 거래 내역 통계</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', fontSize: '14px' }}>
                    <div>
                      <strong>총 거래 건수:</strong> {tradeStats.total}건
                    </div>
                    <div>
                      <strong>매수:</strong> {tradeStats.buy}건
                    </div>
                    <div>
                      <strong>매도:</strong> {tradeStats.sell}건
                    </div>
                    <div>
                      <strong>체결 완료:</strong> {tradeStats.completed}건
                    </div>
                    <div>
                      <strong>대기 중:</strong> {tradeStats.pending}건
                    </div>
                    <div>
                      <strong>총 거래 금액:</strong> ₩{tradeStats.total_amount?.toLocaleString() || '0'}
                    </div>
                  </div>
                </div>
              )}

              {/* 최근 거래 내역 */}
              {loadingTrades ? (
                <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                  거래 내역을 불러오는 중...
                </div>
              ) : aiTrades.length > 0 ? (
                <div style={{ marginBottom: '20px' }}>
                  <h3 style={{ marginTop: 0, marginBottom: '15px', fontSize: '16px' }}>📋 최근 거래 내역 (최대 10건)</h3>
                  <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #dee2e6', borderRadius: '5px' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead style={{ backgroundColor: '#f8f9fa', position: 'sticky', top: 0 }}>
                        <tr>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>일시</th>
                          <th style={{ padding: '10px', textAlign: 'left', borderBottom: '2px solid #dee2e6' }}>종목</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>구분</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>수량</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>가격</th>
                          <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #dee2e6' }}>금액</th>
                          <th style={{ padding: '10px', textAlign: 'center', borderBottom: '2px solid #dee2e6' }}>상태</th>
                        </tr>
                      </thead>
                      <tbody>
                        {aiTrades.slice(0, 10).map((trade, index) => (
                          <tr key={trade._id || index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                            <td style={{ padding: '10px' }}>
                              {new Date(trade.createdAt).toLocaleString('ko-KR', { 
                                year: 'numeric', 
                                month: '2-digit', 
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </td>
                            <td style={{ padding: '10px', fontWeight: '600' }}>
                              {trade.stock_name || trade.symbol}
                            </td>
                            <td style={{ 
                              padding: '10px', 
                              textAlign: 'center',
                              color: trade.order_type === 'buy' ? '#28a745' : '#dc3545',
                              fontWeight: '600'
                            }}>
                              {trade.order_type === 'buy' ? '매수' : '매도'}
                            </td>
                            <td style={{ padding: '10px', textAlign: 'right' }}>
                              {trade.quantity?.toLocaleString() || '0'}주
                            </td>
                            <td style={{ padding: '10px', textAlign: 'right' }}>
                              ₩{trade.price?.toLocaleString() || '0'}
                            </td>
                            <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600' }}>
                              {(() => {
                                // 매도 주문의 경우 total_amount가 잘못 저장될 수 있으므로 직접 계산
                                const isSellOrder = trade.order_type === 'sell';
                                const quantity = Number(trade.quantity || 0);
                                const price = Number(trade.price || 0);
                                let totalAmount;
                                
                                if (isSellOrder) {
                                  // 매도 주문: 항상 수량 * 가격으로 계산
                                  totalAmount = quantity * price;
                                } else {
                                  // 매수 주문: total_amount 우선 사용, 없으면 계산
                                  totalAmount = trade.total_amount || (quantity * price) || 0;
                                }
                                
                                // 100원 단위 절삭
                                totalAmount = Math.floor(totalAmount / 100) * 100;
                                
                                return `₩${totalAmount.toLocaleString() || '0'}`;
                              })()}
                            </td>
                            <td style={{ padding: '10px', textAlign: 'center' }}>
                              <span style={{
                                padding: '4px 8px',
                                borderRadius: '3px',
                                fontSize: '12px',
                                backgroundColor: trade.status === 'completed' ? '#d4edda' :
                                               trade.status === 'pending' ? '#fff3cd' :
                                               trade.status === 'cancelled' ? '#f8d7da' : '#e2e3e5',
                                color: trade.status === 'completed' ? '#155724' :
                                       trade.status === 'pending' ? '#856404' :
                                       trade.status === 'cancelled' ? '#721c24' : '#383d41'
                              }}>
                                {trade.status === 'completed' ? '완료' :
                                 trade.status === 'pending' ? '대기' :
                                 trade.status === 'cancelled' ? '취소' : trade.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div style={{ marginBottom: '20px', padding: '20px', textAlign: 'center', color: '#666', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  AI가 실행한 거래 내역이 없습니다.
                </div>
              )}

              {/* 확인 버튼 */}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => setShowStopConfirm(false)}
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  유지
                </button>
                <button
                  type="button"
                  onClick={handleStopConfirm}
                  disabled={loading}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    fontWeight: '600'
                  }}
                >
                  {loading ? '중단 중...' : '중단'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AITradeConfigModal;
